import React, { useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import {
  Box, Drawer, List, ListItem, ListItemIcon,
  ListItemText, Typography, AppBar, Toolbar,
  Card, CardContent, Grid, Divider, Chip
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChecklistIcon from "@mui/icons-material/Checklist";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedIcon from "@mui/icons-material/Verified";
import SecurityIcon from "@mui/icons-material/Security";
import LogoutIcon from "@mui/icons-material/Logout";

const DRAWER_WIDTH = 240;

const menuItems = [
  { text: "My Dashboard",         icon: <DashboardIcon />, id: "dashboard" },
  { text: "Onboarding Checklist", icon: <ChecklistIcon />, id: "onboarding" },
  { text: "My Profile",           icon: <PersonIcon />,    id: "profile" },
  { text: "My Certifications",    icon: <VerifiedIcon />,  id: "certifications" },
];

const checklistItems = [
  { task: "Complete personal information form", done: false },
  { task: "Upload ID document", done: false },
  { task: "Upload background check consent", done: false },
  { task: "Complete firearms safety training", done: false },
  { task: "Submit certifications", done: false },
];

export default function EmployeeDashboard({ user }) {
  const [activePage, setActivePage] = useState("dashboard");

  const handleLogout = async () => {
    await signOut(auth);
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* Sidebar */}
      <Drawer variant="permanent" sx={{
        width: DRAWER_WIDTH,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          bgcolor: "#01579b",
          color: "white",
        }
      }}>
        <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1 }}>
          <SecurityIcon sx={{ color: "white", fontSize: 30 }} />
          <Typography variant="h6" fontWeight="bold" color="white" fontSize={14}>
            HQ Scan Security
          </Typography>
        </Box>
        <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />

        <List>
          {menuItems.map((item) => (
            <ListItem button key={item.id}
              onClick={() => setActivePage(item.id)}
              sx={{
                bgcolor: activePage === item.id
                  ? "rgba(255,255,255,0.2)" : "transparent",
                borderRadius: 2, mx: 1, mb: 0.5,
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
              }}>
              <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text}
                primaryTypographyProps={{ fontSize: 13 }} />
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: "auto", p: 2 }}>
          <Divider sx={{ bgcolor: "rgba(255,255,255,0.2)", mb: 1 }} />
          <ListItem button onClick={handleLogout}
            sx={{ borderRadius: 2, "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}>
            <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout"
              primaryTypographyProps={{ fontSize: 13 }} />
          </ListItem>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        <AppBar position="static" elevation={0}
          sx={{ bgcolor: "white", color: "black" }}>
          <Toolbar>
            <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
              {menuItems.find(m => m.id === activePage)?.text}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          {activePage === "dashboard" && (
            <>
              <Typography variant="h5" fontWeight="bold" mb={3}>
                Welcome 👋
              </Typography>

              {/* Status Cards */}
              <Grid container spacing={3} mb={4}>
                {[
                  { label: "Onboarding Status", value: "In Progress", color: "#e65100" },
                  { label: "Active Certifications", value: "0", color: "#1b5e20" },
                  { label: "Expiring Soon", value: "0", color: "#b71c1c" },
                  { label: "Documents Uploaded", value: "0", color: "#1a237e" },
                ].map((stat) => (
                  <Grid item xs={12} sm={6} md={3} key={stat.label}>
                    <Card elevation={2} sx={{ borderRadius: 3 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                        <Typography variant="h5" fontWeight="bold"
                          sx={{ color: stat.color, mt: 1 }}>
                          {stat.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Onboarding Checklist Preview */}
              <Card elevation={2} sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Onboarding Checklist
                </Typography>
                {checklistItems.map((item, index) => (
                  <Box key={index} sx={{
                    display: "flex", alignItems: "center",
                    gap: 2, py: 1.5,
                    borderBottom: index < checklistItems.length - 1
                      ? "1px solid #eee" : "none"
                  }}>
                    <Chip
                      label={item.done ? "Done" : "Pending"}
                      size="small"
                      sx={{
                        bgcolor: item.done ? "#e8f5e9" : "#fff3e0",
                        color: item.done ? "#2e7d32" : "#e65100",
                        fontWeight: "bold", minWidth: 70
                      }}
                    />
                    <Typography variant="body2">{item.task}</Typography>
                  </Box>
                ))}
              </Card>
            </>
          )}

          {activePage !== "dashboard" && (
            <Box sx={{
              display: "flex", alignItems: "center",
              justifyContent: "center", height: "60vh",
              flexDirection: "column", gap: 2
            }}>
              <Typography variant="h5" color="text.secondary">
                {menuItems.find(m => m.id === activePage)?.text}
              </Typography>
              <Typography color="text.secondary">
                This module is coming soon...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}